import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Stack, Typography, Button, IconButton, TextField, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../services/api";
import CardTimelineDrawer from "../components/CardTimelineDrawer";
import SearchUserAutocomplete from "../components/SearchUserAutocomplete";

/* utils */
const toLicenca = (chave) => `/licencas/novo/${encodeURIComponent(chave)}`;

const orderCols = (list) => [...list].sort((a,b) => {
  if (a.key_name === 'novo' && b.key_name !== 'novo') return -1;
  if (b.key_name === 'novo' && a.key_name !== 'novo') return 1;
  return (a.posicao - b.posicao) || (a.id - b.id);
});

const contrastFor = (hex) => {
  const c = (hex||'#ffffff').replace('#','');
  const r = parseInt(c.slice(0,2),16), g = parseInt(c.slice(2,4),16), b = parseInt(c.slice(4,6),16);
  const yiq = (r*299 + g*587 + b*114) / 1000;
  return yiq >= 128 ? '#111' : '#fff';
};

/* ===== Card (kanban item) ===== */
function CardItem({ card, onArchive, onNew, onOpenDetails, cobrancaColor }) {
  const isCobranca = card.origem === 'vencida';
  return (
    <Paper
      variant="outlined"
      onClick={() => onOpenDetails?.()}
      tabIndex={0}
      onKeyDown={(e)=> (e.key==='Enter'||e.key===' ') && onOpenDetails?.()}
      sx={{
        p: 1,
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        ...(isCobranca ? { borderLeft: `4px solid ${cobrancaColor || '#ffebee'}` } : {})
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              noWrap
              title={card.cliente_nome_snapshot || ""}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {card.cliente_nome_snapshot || "—"}
            </Typography>
            {isCobranca && (
              <Chip
                size="small"
                label="Cobrança"
                sx={{
                  bgcolor: cobrancaColor || '#ffebee',
                  color: contrastFor(cobrancaColor || '#ffebee'),
                  height: 20,
                  flexShrink: 0
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'nowrap', minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily:"ui-monospace,Menlo,monospace", overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={card.chave_licenca}
            >
              {card.chave_licenca}
            </Typography>
            <IconButton
              size="small"
              onClick={(e)=>{ e.stopPropagation(); window.open(toLicenca(card.chave_licenca), '_blank'); }}
              title="Abrir licença"
            >
              <OpenInNewIcon fontSize="inherit" />
            </IconButton>
          </Stack>

          <Typography variant="caption" noWrap>
            {card.telefone_snapshot || "—"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Tooltip title="Novo card nesta coluna">
            <IconButton size="small" onClick={(e)=>{e.stopPropagation(); onNew?.(card.coluna_key);}}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Arquivar">
            <IconButton size="small" onClick={(e)=>{e.stopPropagation(); onArchive(card);}}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

/* ===== Dialog: novo card ===== */
function NovoCardDialog({ open, onClose, colunaKey, onCreated }) {
  const [chave, setChave] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ if(open){ setChave(""); setObs(""); } }, [open]);

  const salvar = async () => {
    if (!chave.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/kanban/cards", {
        chave_licenca: chave.trim(),
        coluna_key: colunaKey,
        origem: "manual",
        observacao: obs || null
      });
      if (data?.card) onCreated?.(data.card);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.error || "Não foi possível criar o card.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose?.()} maxWidth="xs" fullWidth>
      <DialogTitle>Novo card</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Chave da licença"
          fullWidth
          value={chave}
          onChange={e=>setChave(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Observação (opcional)"
          fullWidth
          value={obs}
          onChange={e=>setObs(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={saving}>
          {saving ? "Salvando..." : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===== Dialog: gerenciar colunas (DnD nas linhas) ===== */
function GerenciarColunasDialog({ open, onClose, columns, reload, owner, isAdmin, bindApi }) {
  const sortCols = (list) => orderCols(list);

  const [rows, setRows] = useState(sortCols(columns));
  const [novoTitulo, setNovoTitulo] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => setRows(sortCols(columns)), [columns]);

  // Persistência da ordem das colunas
  const persistOrder = async (ordered) => {
    const list = [...ordered];

    // força "novo" a ficar em primeiro
    const idxNovo = list.findIndex(c => c.key_name === "novo");
    if (idxNovo > 0) list.unshift(list.splice(idxNovo, 1)[0]);

    // posições 10,20,30… (novo=10)
    const wants = new Map();
    list.forEach((c, i) => wants.set(c.id, (i + 1) * 10));

    const patches = list
      .filter(c => Number(c.posicao) !== wants.get(c.id))
      .map(c => api.patch(`/kanban/columns/${c.id}`, { posicao: wants.get(c.id) }));

    if (patches.length === 0) return;

    try {
      await Promise.all(patches);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao salvar nova ordem.");
      await reload();
    }
  };

  // helpers simples (cores / título / criar / remover)
  const updateColor = async (id, value) => {
    setRows(prev => prev.map(x => x.id === id ? { ...x, cor_hex: value } : x));
    try {
      await api.patch(`/kanban/columns/${id}`, { cor_hex: value });
    } catch (e) {
      alert(e?.response?.data?.error || 'Erro ao salvar cor');
      await reload();
    }
  };

  const salvarTitulo = async (r) => {
    try {
      await api.patch(`/kanban/columns/${r.id}`, { titulo: r.titulo });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erro ao renomear');
    }
  };

  const criar = async () => {
    if (!novoTitulo.trim()) return;
    setCreating(true);
    try {
      const payload = { titulo: novoTitulo.trim() };
      if (isAdmin) {
        const ow = String(owner || '').trim();
        if (!ow || ow.toUpperCase() === 'GLOBAL') payload.global = true;
        else payload.owner = ow;
      }
      await api.post('/kanban/columns', payload);
      setNovoTitulo("");
      await reload();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erro ao criar coluna');
    } finally {
      setCreating(false);
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta coluna?')) return;
    try {
      await api.delete(`/kanban/columns/${id}`);
      await reload();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.need_move_to) {
        const dest = prompt(
          `Esta coluna possui ${e.response.data.count} cards. Mover para qual coluna (key_name)?`,
          'novo'
        );
        if (dest) {
          await api.delete(`/kanban/columns/${id}`, { params: { move_to: dest } });
          await reload();
          return;
        }
      }
      alert(e?.response?.data?.error || 'Erro ao remover');
    }
  };

  // API exposta ao pai para reordenar via DnD do DragDropContext externo
  useEffect(() => {
    bindApi?.({
      reorder: async (fromIndex, toIndex) => {
        const list = Array.from(rows);
        const [moved] = list.splice(fromIndex, 1);
        let dest = toIndex;
        if (moved.key_name !== 'novo' && dest === 0) dest = 1; // “novo” sempre no topo
        list.splice(dest, 0, moved);
        setRows(list);
        await persistOrder(list);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, bindApi]);

  const viewingLabel = (() => {
    const ow = (owner || '').trim();
    if (ow === '' || ow.toUpperCase() === 'GLOBAL') return 'Editando colunas GLOBAIS';
    return `Editando colunas do usuário: ${ow}`;
  })();

  const disableAdd = creating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gerenciar colunas</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary">
          {viewingLabel}. A coluna <b>novo</b> é fixa e não pode ser renomeada/removida/movida.
          <br/>Dica: <b>clique e arraste</b> uma linha para reordenar.
        </Typography>

        <Box mt={2} />

        {/* Lista de colunas arrastável (tipo COLUMNS) */}
        <Droppable droppableId="cols-list" type="COLUMNS">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {rows.map((r, idx) => {
                const isFixed = r.key_name === 'novo';
                return (
                  <Draggable
                    key={String(r.id)}
                    draggableId={String(r.id)}
                    index={idx}
                    isDragDisabled={isFixed || !r.can_edit}
                  >
                    {(prov, snapshot) => (
                      <Paper
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        variant="outlined"
                        sx={{
                          p:1,
                          cursor: isFixed ? 'not-allowed' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                          opacity: isFixed ? 0.9 : 1
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip size="small" label={r.key_name} />

                          <TextField
                            size="small"
                            sx={{ flex:1 }}
                            value={r.titulo}
                            onChange={(e)=>setRows(prev => prev.map(x => x.id===r.id ? {...x, titulo:e.target.value} : x))}
                            onBlur={()=>{
                              if (r.key_name === 'cobranca' || isFixed || !r.can_edit) return;
                              const curr = rows.find(x=>x.id===r.id);
                              if (curr) salvarTitulo(curr);
                            }}
                            disabled={isFixed || !r.can_edit || r.key_name === 'cobranca'}
                          />

                          <TextField
                            type="color"
                            size="small"
                            value={r.cor_hex || '#f5f5f5'}
                            onChange={(e)=>updateColor(r.id, e.target.value)}
                            sx={{ width: 56, minWidth: 56, p:0 }}
                            disabled={!r.can_edit}
                          />

                          <Tooltip title="Remover">
                            <span>
                              <IconButton
                                size="small"
                                onClick={()=>remover(r.id)}
                                disabled={isFixed || r.key_name === 'cobranca' || !r.can_edit}
                              >
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>

        <Divider sx={{ my:1.5 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Nova coluna"
            value={novoTitulo}
            onChange={(e)=>setNovoTitulo(e.target.value)}
            sx={{ flex:1 }}
          />
          <Button startIcon={<AddIcon />} variant="contained"
            onClick={criar}
            disabled={creating || disableAdd}
          >
            Adicionar
          </Button>
          {disableAdd && (
            <Typography variant="caption" color="text.secondary" sx={{ ml:1 }}>
              Admin: criação diretamente no board do usuário está desativada.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===== Dialog: arquivados ===== */
function ArquivadosDialog({ open, onClose, onRestore }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState("");
  const [deletingId, setDeletingId] = useState(null); // <- novo

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/kanban/archived', { params: { limit: 200 } });
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(()=>{ if(open) carregar(); }, [open]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.chave_licenca || '').toLowerCase().includes(q) ||
      (r.cliente_nome_snapshot || '').toLowerCase().includes(q)
    );
  }, [rows, busca]);

  const restaurar = async (card) => {
    await api.patch(`/kanban/cards/${card.id}/unarchive`, { coluna_key: 'novo' });
    onRestore?.();      // recarrega board
    await carregar();   // recarrega lista de arquivados
  };

  // <- novo: excluir definitivamente (só para arquivados)
  const excluir = async (card) => {
    if (!window.confirm('Excluir permanentemente este card arquivado? Esta ação não pode ser desfeita.')) return;
    try {
      setDeletingId(card.id);
      await api.delete(`/kanban/cards/${card.id}`);
      // remove localmente para experiência instantânea
      setRows(prev => prev.filter(r => r.id !== card.id));
      // se quiser, também recarrega board (provavelmente desnecessário):
      // onRestore?.();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erro ao excluir.');
      await carregar();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Arquivados</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb:1 }}>
          <TextField size="small" placeholder="Buscar por chave/nome..." value={busca} onChange={e=>setBusca(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr:1 }} /> }} sx={{ flex:1 }} />
        </Stack>

        {loading ? <Typography>Carregando...</Typography> : (
          filtradas.length === 0 ? <Typography variant="body2" color="text.secondary">Sem arquivados.</Typography> : (
            filtradas.map(c => (
              <Paper key={c.id} variant="outlined" sx={{ p:1, mb:1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ flex:1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{c.cliente_nome_snapshot || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily:'ui-monospace,Menlo,monospace' }}>
                      {c.chave_licenca}
                    </Typography>
                  </Box>

                  <Tooltip title="Desarquivar (volta para NOVO)">
                    <IconButton onClick={()=>restaurar(c)}><UnarchiveIcon /></IconButton>
                  </Tooltip>

                  {/* novo: lixeira */}
                  <Tooltip title="Excluir definitivamente">
                    <span>
                      <IconButton
                        onClick={()=>excluir(c)}
                        disabled={deletingId === c.id}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Paper>
            ))
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===== Página principal ===== */
export default function KanbanClientes() {
  const [columns, setColumns] = useState([]);
  const [cardsByCol, setCardsByCol] = useState({});
  const [loading, setLoading] = useState(false);

  const [ownerSel, setOwnerSel] = useState(null);

  const [dlgNovoOpen, setDlgNovoOpen] = useState(false);
  const [novoCol, setNovoCol] = useState(null);

  const [dlgColsOpen, setDlgColsOpen] = useState(false);
  const [dlgArchOpen, setDlgArchOpen] = useState(false);

  const [busca, setBusca] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);
  const [owner, setOwner] = useState('');
  const [ownerInput, setOwnerInput] = useState('');

  const cobrancaColor = useMemo(
    () => (columns.find(c => c.key_name === 'cobranca')?.cor_hex) || '#ffebee',
    [columns]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const openDetails = (card) => { setSelectedCard(card); setDrawerOpen(true); };

  // API para dialog de colunas
  const dialogApi = useRef(null);

  // 1) pode manter esse efeito, mas ele vira "fallback":
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        // aceita variações comuns
        const t = (data?.tipo || data?.role || '').toString().toLowerCase();
        setIsAdmin(t === 'admin' || data?.is_admin === true);
      } catch {
        setIsAdmin(false);
      }
    })();
  }, []);


  // 2) no carregar(), leia a flag vinda do /kanban/board
  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      // continue mandando owner se já estiver setado
      if (owner) params.owner = owner;
  
      const { data } = await api.get("/kanban/board", { params });
  
      setColumns(data.columns || []);
      setCardsByCol(data.cards || {});
      setIsAdmin(Boolean(data.for_admin)); // <- chave do problema: liga o modo admin no UI
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, [owner]);

  const filteredByCol = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return cardsByCol;
    const out = {};
    Object.keys(cardsByCol).forEach(k => {
      out[k] = (cardsByCol[k] || []).filter(c =>
        (c.chave_licenca || '').toLowerCase().includes(q) ||
        (c.cliente_nome_snapshot || '').toLowerCase().includes(q)
      );
    });
    return out;
  }, [cardsByCol, busca]);

  const openNovo = (colKey) => { setNovoCol(colKey); setDlgNovoOpen(true); };
  const onCreatedCard = (card) => {
    setCardsByCol(prev => {
      const next = { ...prev };
      next[card.coluna_key] = [card, ...(next[card.coluna_key] || [])];
      return next;
    });
  };

  const onArchive = async (card) => {
    const ok = window.confirm("Arquivar este card?");
    if (!ok) return;
    setCardsByCol(prev => {
      const next = { ...prev };
      next[card.coluna_key] = (next[card.coluna_key] || []).filter(c => c.id !== card.id);
      return next;
    });
    try { await api.patch(`/kanban/cards/${card.id}/archive`); }
    catch { alert('Falha ao arquivar. Atualizando…'); carregar(); }
  };

  const onOpenLic = (card) => window.open(toLicenca(card.chave_licenca), '_blank');

  // mover cards entre colunas
  const onCardsDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId &&
        destination.index === source.index) return;

    const from = source.droppableId, to = destination.droppableId;
    const movingList = { ...cardsByCol };

    const original = movingList[from][source.index];
    const item = { ...original, coluna_key: to };

    movingList[from] = Array.from(movingList[from]);
    movingList[from].splice(source.index, 1);

    movingList[to] = Array.from(movingList[to] || []);
    movingList[to].splice(destination.index, 0, item);

    setCardsByCol(movingList);

    try { await api.patch(`/kanban/cards/${draggableId}/move`, { coluna_key: to }); }
    catch { alert("Falha ao mover. Recarregando…"); carregar(); }
  };

  // roteia pelo type do DnD
  const handleDragEnd = (result) => {
    const { destination, type } = result;
    if (!destination) return;

    if (type === 'COLUMNS') {
      // DnD dentro do diálogo de colunas
      dialogApi.current?.reorder(result.source.index, destination.index);
      return;
    }

    // DnD de cards
    onCardsDragEnd(result);
  };

  const countIn = (k) => (filteredByCol[k]?.length || 0);

  const applyOwnerInput = () => setOwner(ownerInput.trim());
  const setGlobalOwner = () => { setOwner(''); setOwnerInput(''); };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box p={2}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb:2 }}>
          <Typography variant="h5" fontWeight={800}>Kanban de Licenças</Typography>

          {/* empurra tudo à direita */}
          <Box sx={{ flex:1 }} />

          {/* Busca com largura fixa (não fica minúscula nem ocupa a tela toda) */}
          <TextField
            size="small"
            placeholder="Buscar por chave/nome…"
            value={busca}
            onChange={e=>setBusca(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr:1 }} /> }}
            sx={{ width: 280 }}
          />

          {isAdmin && (
            <>
              {/* GLOBAL entre a busca e o autocomplete */}
              <Button variant="outlined" onClick={setGlobalOwner}>GLOBAL</Button>

              {/* Autocomplete de revendedor/licença */}
              <SearchUserAutocomplete
                value={ownerSel}
                onChange={(val) => {
                  setOwnerSel(val);
                  const chave = val?.chave || "";
                  setOwnerInput(chave);
                  setOwner(chave); // dispara carregar()
                }}
                sx={{ width: 320 }}
                TextFieldProps={{ placeholder: 'Buscar revendedor/licença' }}
              />

              {/* Campo manual + VER (opcional) */}
              <Tooltip title='Admin: deixe vazio ou "GLOBAL" para ver globais; ou informe a chave do usuário.'>
                <TextField
                  size="small"
                  label="Owner (admin)"
                  placeholder="GLOBAL ou chave_usuario"
                  value={ownerInput}
                  onChange={(e) => setOwnerInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") applyOwnerInput(); }}
                  sx={{ width: 260 }}
                />
              </Tooltip>
              <Button onClick={applyOwnerInput}>Ver</Button>
            </>
          )}

          <Button startIcon={<UnarchiveIcon />} onClick={()=>setDlgArchOpen(true)}>Arquivados</Button>
          <Button startIcon={<SettingsIcon />} onClick={()=>setDlgColsOpen(true)}>Gerenciar colunas</Button>
        </Stack>

        {/* Board de cards */}
        <Stack direction="row" gap={2} alignItems="stretch" sx={{ overflowX: 'auto' }}>
          {orderCols(columns).map(col => {
            const list = filteredByCol[col.key_name] || [];
            const bg = col.cor_hex || '#f5f5f5';
            const fg = contrastFor(bg);

            return (
              <Paper key={col.key_name} variant="outlined" sx={{ minWidth: 320, maxWidth: 380, flex: '0 0 auto' }}>
                <Box sx={{
                  px:1.5, py:1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: bg,
                  color: fg
                }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" fontWeight={800} noWrap title={col.titulo}>
                      {col.titulo}
                    </Typography>
                    <Chip size="small" label={countIn(col.key_name)} sx={{ bgcolor: 'rgba(0,0,0,0.1)', color: fg }} />
                    <Box sx={{ ml: 'auto' }} />
                    <Button size="small" startIcon={<AddIcon />} onClick={()=>openNovo(col.key_name)} sx={{ color: fg }}>
                      Novo
                    </Button>
                  </Stack>
                </Box>

                <Droppable droppableId={col.key_name} type="CARDS">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        p: 1.5,
                        minHeight: 140,
                        backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}
                    >
                      {list.length === 0 && (
                        <Typography variant="caption" color="text.secondary">Sem cards</Typography>
                      )}

                      {list.map((card, index) => (
                        <Draggable key={String(card.id)} draggableId={String(card.id)} index={index}>
                        {(prov, snapshot) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            style={{ ...prov.draggableProps.style, boxSizing: 'border-box' }}
                          >
                            <CardItem
                              card={card}
                              onArchive={onArchive}
                              onNew={(colKey) => openNovo(colKey)}
                              cobrancaColor={cobrancaColor}
                              onOpenDetails={() => openDetails(card)}
                            />
                          </div>
                        )}
                        </Draggable>
                      ))}

                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            );
          })}
        </Stack>
      </Box>

      {/* diálogos dentro do mesmo DragDropContext */}
      <NovoCardDialog
        open={dlgNovoOpen}
        onClose={()=>setDlgNovoOpen(false)}
        colunaKey={novoCol}
        onCreated={onCreatedCard}
      />

      <GerenciarColunasDialog
        open={dlgColsOpen}
        onClose={()=>setDlgColsOpen(false)}
        columns={columns}
        reload={carregar}
        owner={owner}
        isAdmin={isAdmin}
        bindApi={(api) => (dialogApi.current = api)}
      />

      <ArquivadosDialog
        open={dlgArchOpen}
        onClose={()=>setDlgArchOpen(false)}
        onRestore={carregar}
      />

      <CardTimelineDrawer
        key={selectedCard?.id || 'drawer'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        card={selectedCard}
      />
    </DragDropContext>
  );
}
